/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {GOOGLE_MAPS_EMBED_URL, GOOGLE_MAPS_URL} from '../constants';
import {
  EARTH_RADIUS,
  GEOFENCE_BUFFER_FACTORIAL,
  RECTANGULAR_GEOFENCE_INITIAL_BUFFER
} from '../geofences/constants';

export enum NavigationView {
  STREET = 'STREET',
  NAVIGATION = 'Navigation'
};

/**
 * @getMetersInDegree
 *
 * Takes an argument of desired meters and returns the
 * degrees reading for use as lat/long. So if you wanted
 * 10 meters returned as a degree reading you would pass
 * 10 as the argument
 *
 * ```typescript
 *
 *   import {getMetersInDegree} from '@ion/dls/src/maps/helpers';
 *
 *   const degrees = getMetersInDegree(10);
 *
 */
export const getMetersInDegree = (meters) => {
  return 1 / (((2 * Math.PI) / 360) * EARTH_RADIUS) / (1000 / meters);
};

/**
 * Converts a given distance in **kilometers** to its approximate **degree value**
 * for latitude/longitude calculations.  
 *
 * This is useful when you need to create geographic buffers or radius-based
 * computations (for example, converting a radius in kilometers into a degree
 * value usable by mapping APIs).
 *
 * ```typescript
 * import {getKmInDegree} from '@ion/dls/src/maps/helpers';
 *
 * const degrees = getKmInDegree(5); // Convert 5 km to degrees
 * ```
 *
 * @param km - The distance in kilometers.
 * @returns The equivalent distance in degrees.
 */
export const getKmInDegree = (km: number): number => {
  const meters = km * 1000;
  return getMetersInDegree(meters);
};

/**
 * Converts a given distance in **miles** to its approximate **degree value**
 * for latitude/longitude computations.  
 *
 * This is typically used when working with map tools or APIs that expect
 * distances in degrees instead of miles.
 *
 * ```typescript
 * import {getMilesInDegree} from '@ion/dls/src/maps/helpers';
 *
 * const degrees = getMilesInDegree(3.1); // Convert 3.1 miles to degrees
 * ```
 *
 * @param miles - The distance in miles.
 * @returns The equivalent distance in degrees.
 */
export const getMilesInDegree = (miles: number): number => {
  const meters = miles * 1609.344;
  return getMetersInDegree(meters);
};

/**
 * @getExponentialBuffer
 *
 * Uses negative exponetiation to calculate the buffer needed
 * (in vertices) for a drawing graphic based on the zoom level provided. Basically, the
 * smaller the zoom level (the farther out) the higher the buffer would be
 * based on a starting buffer at zoom 1 of 700,000.
 *
 * ```typescript
 *
 *   import {getExponentialBuffer} from '@ion/dls/src/maps/helpers';
 *
 *   const buffer = getExponentialBuffer(10); // 2598.46
 *
 */
export const getExponentialBuffer = (zoom: number) => {
  return RECTANGULAR_GEOFENCE_INITIAL_BUFFER * (GEOFENCE_BUFFER_FACTORIAL ** -zoom);
};

/**
 * Describes the position a Street View panorama is opened at. Latitude and
 * longitude are widened to accept strings because the tracking feed returns
 * stringified coordinates on some payloads.
 */
export interface StreetViewPoint {
  latitude?: number | string;
  longitude?: number | string;
  heading?: number;
}

const DEFAULT_STREET_VIEW_HEADING = 0;

/**
 * @getStreetViewHeading
 *
 * Returns the compass heading a panorama should face, rounded to the nearest
 * degree, falling back to due north when the feed reported no heading.
 *
 * ```typescript
 *
 *   import {getStreetViewHeading} from '@ion/dls/src/maps/helpers/globeUtils';
 *
 *   const heading = getStreetViewHeading({heading: 232.32}); // 232
 *
 * ```
 *
 * @param point the position to read the heading from
 * @returns the heading in degrees
 */
export const getStreetViewHeading = (point: StreetViewPoint): number => (
  Number.isFinite(point?.heading) ? Math.round(Number(point.heading)) : DEFAULT_STREET_VIEW_HEADING
);

/**
 * @buildStreetViewShareUrl
 *
 * Builds the shareable Google Maps panorama deep link for a position. This is
 * the human-facing URL: it is what `navigateToGoogleStreetView` opens, what the
 * Street View dialog's "open in Google Maps" action targets, and what its "copy
 * link" action writes to the clipboard.
 *
 * ```typescript
 *
 *   import {buildStreetViewShareUrl} from '@ion/dls/src/maps/helpers/globeUtils';
 *
 *   const url = buildStreetViewShareUrl({
 *      latitude: 48.857832,
 *      longitude: 2.295226
 *   });
 *
 * ```
 *
 * @param point the position to build the link for
 * @returns the Google Maps panorama URL
 */
export const buildStreetViewShareUrl = ({latitude, longitude}: StreetViewPoint): string => (
  `${GOOGLE_MAPS_URL}/@?api=1&map_action=pano&viewpoint=${latitude},${longitude}`
);

/**
 * @buildStreetViewEmbedUrl
 *
 * Builds the URL rendered inside the Street View dialog's iframe, facing the
 * direction the asset was last travelling.
 *
 * This uses Google's `output=svembed` form, which resolves a panorama directly
 * from a coordinate pair and requires no API key. It is not part of any
 * published Google API and carries no compatibility guarantee; the supported
 * alternative is the Maps Embed API, which requires a provisioned, referrer
 * restricted, per-load billed key. The keyless form is a deliberate choice for
 * ION-7366 — if the panorama ever renders blank, verify this URL first.
 *
 * ```typescript
 *
 *   import {buildStreetViewEmbedUrl} from '@ion/dls/src/maps/helpers/globeUtils';
 *
 *   const src = buildStreetViewEmbedUrl({
 *      latitude: 48.857832,
 *      longitude: 2.295226,
 *      heading: 232
 *   });
 *
 * ```
 *
 * @param point the position to embed a panorama for
 * @returns the embeddable panorama URL
 */
export const buildStreetViewEmbedUrl = (point: StreetViewPoint): string => (
  `${GOOGLE_MAPS_EMBED_URL}?q=&layer=c&cbll=${point?.latitude},${point?.longitude}` +
  `&cbp=11,${getStreetViewHeading(point)},0,0,0&output=svembed`
);

/**
 * @navigateToGoogleStreetView
 *
 * Open new tab and navigate to Google street by given latitude and longitude.
 *
 * ```typescript
 *
 *   import {navigateToGoogleStreetView} from '@ion/dls/src/maps/helpers/globeUtils';
 *
 *   navigateToGoogleStreetView({
 *      latitude: 48.857832,
 *      longitude: 2.295226
 *   });
 */
export const navigateToGoogleStreetView = (point: StreetViewPoint) => {
  window.open(buildStreetViewShareUrl(point), '_blank');
};

/**
 * @navigateToGoogleDirections
 *
 * Open new tab and navigate to Google direction by given latitude and longitude.
 *
 * ```typescript
 *
 *   import {navigateToGoogleDirections} from '@ion/dls/src/maps/helpers';
 *
 *   navigateToGoogleDirections({
 *      latitude: 48.857832,
 *      longitude: 2.295226
 *   });
 */
export const navigateToGoogleDirections = ({latitude, longitude}) => {
  const url = `${GOOGLE_MAPS_URL}/dir/?api=1&destination=${latitude},${longitude}`;
  window.open(url, '_blank');
};
